import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  Sparkles,
  Volume2,
  ShieldCheck,
  PanelLeftClose,
  FilePlus2,
  ArrowRight,
  X,
} from 'lucide-react';
import { ROUTES } from '@/routes/paths';
import { useUIStore } from '@/shared/stores/ui.store';
import { useUser } from '@/shared/hooks/useAuth';
import { searchFeatures } from '@/features/search/globalSearch';
import { effectiveWorkspaceAccess } from '@/shared/permissions/workspaceAccess';
import { playNotificationSound, playSuccessSound } from '@/shared/utils/sound';

export interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: typeof Search;
  action: () => void;
  category: 'Navigation' | 'Quick Actions';
}

/**
 * Used to search only the ~16 nav shortcuts hand-written into this file,
 * ungated by role (it showed "Team & Organization" and "Settings" to every
 * tier regardless of navRoleVisible), while the topbar promised "Search
 * people, clients, deals, projects". Now backed by searchFeatures() — the
 * same role/department/workspace-access-gated search that already covers
 * every one of the ~124 real NAV_GROUPS screens and already had a passing
 * test suite (globalSearch.test.ts) but zero callers until this change.
 *
 * Entity search (a specific lead, invoice, employee by name) is NOT wired
 * here — no whole-app entity index exists yet; the closest thing
 * (Cross-Entity Search, /accounts/search) only covers accounts-domain
 * records. Left as a known gap rather than half-building it.
 */
export function CommandPalette({
  open,
  onClose,
  initialQuery = '',
}: {
  open: boolean;
  onClose: () => void;
  initialQuery?: string;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const toggleSidebar = useUIStore((s) => s.toggleSidebarCollapsed);
  const user = useUser();

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, initialQuery]);

  const quickActions: CommandItem[] = useMemo(
    () => [
      {
        id: 'action-new-quotation',
        title: 'New Quotation',
        subtitle: 'Open the document studio to draft one',
        icon: FilePlus2,
        category: 'Quick Actions',
        action: () => navigate(`${ROUTES.documentEditor}?type=quotation`),
      },
      {
        id: 'action-new-proforma',
        title: 'New Proforma Invoice',
        subtitle: 'Open the document studio to draft one',
        icon: FilePlus2,
        category: 'Quick Actions',
        action: () => navigate(`${ROUTES.documentEditor}?type=proforma`),
      },
      {
        id: 'action-new-sales-order',
        title: 'New Sales Order (Full Form)',
        subtitle: 'Billing address, credit limit, dispatch dates',
        icon: FilePlus2,
        category: 'Quick Actions',
        action: () => navigate(ROUTES.salesOrderCreate),
      },
      {
        id: 'action-toggle-sidebar',
        title: 'Toggle Sidebar Navigation',
        subtitle: 'Collapse or expand the main navigation bar',
        icon: PanelLeftClose,
        category: 'Quick Actions',
        action: () => toggleSidebar(),
      },
      {
        id: 'action-ai-copilot',
        title: 'Open AI Copilot Assistant',
        subtitle: 'Query AI about pipeline, cashflow and team',
        icon: Sparkles,
        category: 'Quick Actions',
        action: () => navigate(ROUTES.aiCopilot),
      },
      {
        id: 'action-check-health',
        title: 'View System Health & Status',
        subtitle: 'Verify live microservice latency & uptime',
        icon: ShieldCheck,
        category: 'Quick Actions',
        action: () => navigate(ROUTES.activity),
      },
      {
        id: 'action-test-sound',
        title: 'Test Notification Chime Sound',
        subtitle: 'Play audio test bell for alerts & events',
        icon: Volume2,
        category: 'Quick Actions',
        action: () => playNotificationSound(true),
      },
    ],
    [navigate, toggleSidebar],
  );

  const navItems: CommandItem[] = useMemo(() => {
    const q = query.trim();
    if (!q) {
      // No query: surface the dashboard as the one always-available shortcut
      // rather than the whole 124-item nav tree unfiltered.
      return [
        {
          id: 'nav-dashboard',
          title: 'Dashboard',
          subtitle: 'Jump to your home screen',
          icon: LayoutDashboard,
          category: 'Navigation',
          action: () => navigate(ROUTES.dashboard),
        },
      ];
    }
    const results = searchFeatures(user?.role, q, user?.department, effectiveWorkspaceAccess(user));
    return results.map((r) => ({
      id: `nav-${r.path}`,
      title: r.label,
      subtitle: r.group,
      icon: r.icon,
      category: 'Navigation' as const,
      action: () => navigate(r.path),
    }));
  }, [query, user, navigate]);

  const filteredItems = useMemo(() => {
    const q = query.toLowerCase().trim();
    const actions = !q
      ? quickActions
      : quickActions.filter(
          (item) => item.title.toLowerCase().includes(q) || (item.subtitle && item.subtitle.toLowerCase().includes(q)),
        );
    return [...navItems, ...actions];
  }, [navItems, quickActions, query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredItems]);

  const handleSelect = (item: CommandItem) => {
    playSuccessSound();
    onClose();
    item.action();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % (filteredItems.length || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % (filteredItems.length || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const current = filteredItems[selectedIndex];
      if (current) handleSelect(current);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-20 p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-100">
      <div
        className="w-full max-w-xl rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input header */}
        <div className="relative flex items-center border-b border-slate-100 px-4 py-3.5">
          <Search className="w-5 h-5 text-slate-400 absolute left-4" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, actions, or tools..."
            className="w-full pl-8 pr-12 text-sm bg-transparent text-slate-900 placeholder-slate-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results list */}
        <div className="max-h-[360px] overflow-y-auto p-2 divide-y divide-slate-50">
          {filteredItems.length === 0 ? (
            <div className="py-10 text-center text-xs text-slate-400">
              No matching pages or actions found for "{query}"
            </div>
          ) : (
            filteredItems.map((item, idx) => {
              const Icon = item.icon;
              const isSelected = idx === selectedIndex;
              return (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl cursor-pointer transition select-none ${
                    isSelected ? 'bg-blue-50 text-blue-900 font-semibold' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`p-2 rounded-lg ${
                        isSelected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-800">{item.title}</div>
                      {item.subtitle && (
                        <div className="text-[11px] text-slate-400 truncate">{item.subtitle}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-300">{item.category === 'Quick Actions' ? 'Action' : ''}</span>
                    {isSelected && <ArrowRight className="w-3.5 h-3.5 text-blue-600" />}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer shortcuts */}
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
          <div className="flex items-center gap-3">
            <span>↑↓ to navigate</span>
            <span>↵ to select</span>
            <span>esc to close</span>
          </div>
          <span className="font-semibold text-slate-500">ZYNX Command Center</span>
        </div>
      </div>
    </div>
  );
}
